import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['selection', 'toggleAll', 'designItem', 'folderItem'];

  #options = new Map();

  get _dimensions() {
    return [];
  }

  get _designItemIdentifier() {
    return '';
  }

  get _folderItemIdentifier() {
    return '';
  }

  selectionTargetConnected(select) {
    this.#eachOption(select, (option, kind, dimension) => {
      this.#options.set(this.#key(kind, option.dataset.itemId, dimension), option);
    });
    this._dimensions.forEach((dimension) => this.#syncCards(dimension));
    this.#afterChange();
  }

  selectionTargetDisconnected(select) {
    this.#eachOption(select, (option, kind, dimension) => {
      this.#options.delete(this.#key(kind, option.dataset.itemId, dimension));
    });
  }

  toggleAllTargetConnected() {
    this.#syncToggleAll();
  }

  designItemTargetConnected(element) {
    this.#withCard(element, this._designItemIdentifier, (card) => {
      this._dimensions.forEach((dimension) => this.#adoptCard(card, dimension));
      this.#afterChange();
    });
  }

  folderItemTargetConnected(element) {
    this.#withCard(element, this._folderItemIdentifier, (folder) => {
      this.#renderFolder(folder);
      this.#afterChange();
    });
  }

  designToggled({ detail: { kind, id, dimension, checked } }) {
    this.#setSelected(kind, id, dimension, checked);
    this.#afterChange();
  }

  folderToggled(event) {
    const { dimension, checked } = event.detail;
    const folder = this.#cardFor(event.target, this._folderItemIdentifier);
    if (!folder) {
      return;
    }

    this.#eachChild(folder, dimension, (kind, id) =>
      this.#setSelected(kind, id, dimension, checked)
    );
    this.#renderFolder(folder);
    this.#afterChange();
  }

  toggleAll(event) {
    const dimension = this.#dimensionOf(event.target);
    const checked = event.target.checked;

    this.#optionsFor(dimension, event.target.dataset.folder).forEach((option) => {
      option.selected = checked;
    });

    this.#syncCards(dimension);
    this.#afterChange();
  }

  _hasSelection() {
    return this._dimensions.some((dimension) => this.#selectedCount(dimension) > 0);
  }

  _selectionChanged(hasSelection) {}

  #adoptCard(card, dimension) {
    const { kind, id } = card.identity;
    const option = this.#option(kind, id, dimension);
    if (!option) {
      return;
    }

    card.setChecked(dimension, option.selected);
  }

  #syncCards(dimension) {
    this.#designCards.forEach((card) => this.#adoptCard(card, dimension));
    this.#folderCards.forEach((folder) => this.#renderFolder(folder));
  }

  #renderFolder(folder) {
    this._dimensions.forEach((dimension) => {
      if (!folder.hasDimension(dimension)) {
        return;
      }

      let total = 0;
      let selected = 0;
      this.#eachChild(folder, dimension, (kind, id) => {
        total += 1;
        if (this.#isSelected(kind, id, dimension)) {
          selected += 1;
        }
      });
      folder.render(dimension, selected, total);
    });
  }

  #eachChild(folder, dimension, fn) {
    const { boardIds, fpViewIds } = folder.childIds;

    boardIds.forEach((id) => {
      if (this.#option('board', id, dimension)) {
        fn('board', id);
      }
    });
    fpViewIds.forEach((id) => {
      if (this.#option('fpView', id, dimension)) {
        fn('fpView', id);
      }
    });
  }

  #eachOption(select, fn) {
    const { dimension, kind } = select.dataset;
    Array.from(select.options).forEach((option) => fn(option, kind, dimension));
  }

  #key(kind, id, dimension) {
    return `${dimension}:${kind}:${id}`;
  }

  #option(kind, id, dimension) {
    return this.#options.get(this.#key(kind, id, dimension));
  }

  #optionsFor(dimension, folder) {
    const options = this.selectionTargets
      .filter((select) => select.dataset.dimension === dimension)
      .flatMap((select) => Array.from(select.options));

    return folder ? options.filter((option) => option.dataset.folder === folder) : options;
  }

  #isSelected(kind, id, dimension) {
    return !!this.#option(kind, id, dimension)?.selected;
  }

  #setSelected(kind, id, dimension, selected) {
    const option = this.#option(kind, id, dimension);
    if (option) {
      option.selected = selected;
    }
  }

  #selectedCount(dimension, folder) {
    return this.#optionsFor(dimension, folder).filter((option) => option.selected).length;
  }

  #withCard(element, identifier, fn) {
    const card = this.#cardFor(element, identifier);
    if (card) {
      fn(card);
      return;
    }
    requestAnimationFrame(() => {
      const retry = this.#cardFor(element, identifier);
      if (retry) {
        fn(retry);
      }
    });
  }

  #cardFor(element, identifier) {
    return this.application.getControllerForElementAndIdentifier(element, identifier);
  }

  #dimensionOf(element) {
    return element.dataset.dimension || this._dimensions[0];
  }

  get #designCards() {
    return this.designItemTargets
      .map((el) => this.#cardFor(el, this._designItemIdentifier))
      .filter(Boolean);
  }

  get #folderCards() {
    return this.folderItemTargets
      .map((el) => this.#cardFor(el, this._folderItemIdentifier))
      .filter(Boolean);
  }

  #syncToggleAll() {
    this.toggleAllTargets.forEach((element) => {
      const options = this.#optionsFor(this.#dimensionOf(element), element.dataset.folder);

      element.checked = options.length > 0 && options.every((option) => option.selected);
    });
  }

  get #currentFolder() {
    return this.toggleAllTargets[0]?.dataset.folder;
  }

  #afterChange() {
    this.#syncToggleAll();

    const hasSelection = this._hasSelection();
    this._selectionChanged(hasSelection);
    this.dispatch('selectionChanged', {
      detail: {
        hasSelection,
        changed: this.#changed,
        current: this.#selectedCount(this._dimensions[0], this.#currentFolder),
      },
    });
  }

  get #changed() {
    return Array.from(this.#options.values()).some(
      (option) => option.selected !== option.defaultSelected
    );
  }
}
